import React from 'react';

import type { SetValue, Winner } from './scoring-match-draft';
import { matchPoints } from './scoring-match-points';
import {
  deltaTone,
  formatDelta,
  type MatchItem,
  type MatchRating,
  type ScoringSystem,
} from './scoring-types';

function sideDelta(rating: MatchRating | null | undefined, side: 1 | 2, winner: Winner): number | null {
  if (!rating || winner === null) {
    return null;
  }

  const view = side === 1 ? rating.side1 : rating.side2;

  return winner === side ? view.deltaIfWin : view.deltaIfLose;
}

interface SideRowProps {
  match: MatchItem;
  side: 1 | 2;
  sets: SetValue[];
  winner: Winner;
  points: number | null;
}

const SideRow: React.FC<SideRowProps> = ({ match, side, sets, winner, points }) => {
  const delta = sideDelta(match.rating, side, winner);
  const rating = side === 1 ? match.rating?.side1 : match.rating?.side2;
  const scored = sets.filter((set) => set.p1 !== '' && set.p2 !== '');

  return (
    <div className={winner === side ? 'scoring-side scoring-side--won' : 'scoring-side'}>
      <span className="scoring-side__name" data-testid={`match-${match.id}-p${side}`}>
        {side === 1 ? match.player1Name : match.player2Name}
      </span>
      <span className="scoring-side__sets">
        {scored.length === 0
          ? '—'
          : scored.map((set, index) => (
              <span key={index} className="scoring-side__set">
                {side === 1 ? set.p1 : set.p2}
              </span>
            ))}
      </span>
      {points !== null && (
        <span
          className="scoring-side__points"
          title="Punkty klasyfikacyjne za sety"
          data-testid={`match-${match.id}-points${side}`}
        >
          {points}
          <span className="scoring-side__points-unit">pkt</span>
        </span>
      )}
      {delta === null ? (
        <span
          className="scoring-delta scoring-delta--range"
          data-testid={`match-${match.id}-delta${side}`}
        >
          {rating ? `${formatDelta(rating.deltaIfWin)} / ${formatDelta(rating.deltaIfLose)}` : ''}
        </span>
      ) : (
        <span className={deltaTone(delta)} data-testid={`match-${match.id}-delta${side}`}>
          {formatDelta(delta)}
        </span>
      )}
    </div>
  );
};

interface Props {
  match: MatchItem;
  sets: SetValue[];
  winner: Winner;
  scoring?: ScoringSystem;
}

export const ScoringMatchSides: React.FC<Props> = ({ match, sets, winner, scoring }) => {
  const points = matchPoints(scoring, sets);

  return (
    <div className="scoring-match__sides">
      <SideRow match={match} side={1} sets={sets} winner={winner} points={points?.side1 ?? null} />
      <SideRow match={match} side={2} sets={sets} winner={winner} points={points?.side2 ?? null} />
    </div>
  );
};

export default ScoringMatchSides;
